/**
 * Remote player representation for multiplayer mode
 * Handles interpolation and rendering of other players' planes
 */

import { Container, Sprite, Text, TextStyle, Texture } from 'pixi.js';

// Color tints for different player colors
const COLOR_TINTS: Record<string, number> = {
  red: 0xff4444,
  blue: 0x4488ff,
  green: 0x44ff44,
  yellow: 0xffff44,
  purple: 0xaa44ff
};

interface PositionData {
  x: number;
  y: number;
  vx: number;
  vy: number;
  distance: number;
  timestamp: number;
}

export class RemotePlayer {
  public userId: number;
  public nickname: string;
  public color: string;
  public alive: boolean = true;
  public finishReason: string | null = null;

  // Graphics
  private container: Container;
  private sprite: Sprite;
  private nameLabel: Text;

  // Position interpolation
  private targetPosition: PositionData | null = null;
  private currentPosition: PositionData = { x: 0, y: 0, vx: 0, vy: 0, distance: 0, timestamp: 0 };
  private interpolationFactor: number = 0.15; // Smoothing factor

  constructor(
    userId: number,
    nickname: string,
    color: string,
    planeTexture: Texture
  ) {
    this.userId = userId;
    this.nickname = nickname;
    this.color = color;

    // Create container
    this.container = new Container();

    // Create plane sprite
    this.sprite = new Sprite(planeTexture);
    this.sprite.anchor.set(0.5);
    this.sprite.scale.set(0.7);

    // Apply color tint
    this.sprite.tint = COLOR_TINTS[color] || 0xffffff;

    this.container.addChild(this.sprite);

    // Create name label
    this.nameLabel = new Text({
      text: nickname,
      style: new TextStyle({
        fontSize: 12,
        fill: COLOR_TINTS[color] || 0xffffff,
        fontWeight: 'bold',
        dropShadow: {
          color: 0x000000,
          blur: 2,
          distance: 1
        }
      })
    });
    this.nameLabel.anchor.set(0.5);
    this.nameLabel.y = -35;
    this.container.addChild(this.nameLabel);
  }

  /**
   * Get the display container
   */
  getContainer(): Container {
    return this.container;
  }

  /**
   * Update position from server
   */
  updatePosition(x: number, y: number, vx: number, vy: number, distance: number): void {
    this.targetPosition = {
      x, y, vx, vy, distance,
      timestamp: Date.now()
    };
  }

  /**
   * Mark player as finished
   */
  setFinished(reason: string, distance: number): void {
    this.alive = false;
    this.finishReason = reason;
    this.currentPosition.distance = distance;

    // Visual feedback
    this.sprite.alpha = 0.5;

    if (reason === 'crashed') {
      this.sprite.tint = 0x666666; // Gray for crashed
    }
  }

  /**
   * Update each frame - interpolate position
   */
  update(_dt: number, cameraX: number): void {
    if (!this.targetPosition) return;

    // Interpolate towards target position
    this.currentPosition.x += (this.targetPosition.x - this.currentPosition.x) * this.interpolationFactor;
    this.currentPosition.y += (this.targetPosition.y - this.currentPosition.y) * this.interpolationFactor;
    this.currentPosition.vx = this.targetPosition.vx;
    this.currentPosition.vy = this.targetPosition.vy;
    this.currentPosition.distance = this.targetPosition.distance;

    // Update visual position (screen coordinates)
    const screenX = this.currentPosition.x - cameraX;
    const screenY = this.currentPosition.y;

    this.container.x = screenX;
    this.container.y = screenY;

    // Rotate based on velocity
    this.sprite.rotation = this.currentPosition.vy * 0.002;

    // Hide if off screen
    this.container.visible = screenX > -100 && screenX < window.innerWidth + 100;
  }

  /**
   * Get current distance
   */
  getDistance(): number {
    return this.currentPosition.distance;
  }

  /**
   * Get current X position
   */
  getX(): number {
    return this.currentPosition.x;
  }

  /**
   * Get current Y position (screen coordinates)
   */
  getY(): number {
    return this.currentPosition.y;
  }

  /**
   * Destroy and cleanup
   */
  destroy(): void {
    this.container.destroy({ children: true });
  }
}

/**
 * Manager for all remote players
 */
export class RemotePlayerManager {
  private players: Map<number, RemotePlayer> = new Map();
  private container: Container;
  private planeTexture: Texture;

  constructor(parentContainer: Container, planeTexture: Texture) {
    this.container = new Container();
    this.planeTexture = planeTexture;
    parentContainer.addChild(this.container);
  }

  /**
   * Add a new remote player
   */
  addPlayer(userId: number, nickname: string, color: string): RemotePlayer {
    if (this.players.has(userId)) {
      return this.players.get(userId)!;
    }

    const player = new RemotePlayer(userId, nickname, color, this.planeTexture);
    this.players.set(userId, player);
    this.container.addChild(player.getContainer());
    return player;
  }

  /**
   * Remove a player
   */
  removePlayer(userId: number): void {
    const player = this.players.get(userId);
    if (player) {
      player.destroy();
      this.players.delete(userId);
    }
  }

  /**
   * Get a player by ID
   */
  getPlayer(userId: number): RemotePlayer | undefined {
    return this.players.get(userId);
  }

  /**
   * Get all players
   */
  getAllPlayers(): RemotePlayer[] {
    return Array.from(this.players.values());
  }

  /**
   * Update all players
   */
  update(dt: number, cameraX: number): void {
    for (const player of this.players.values()) {
      player.update(dt, cameraX);
    }
  }

  /**
   * Update positions from server sync message
   */
  updatePositions(positions: Array<{
    user_id: number;
    x: number;
    y: number;
    vx: number;
    vy: number;
    distance: number;
  }>): void {
    for (const pos of positions) {
      const player = this.players.get(pos.user_id);
      if (player) {
        player.updatePosition(pos.x, pos.y, pos.vx, pos.vy, pos.distance);
      }
    }
  }

  /**
   * Clear all players
   */
  clear(): void {
    for (const player of this.players.values()) {
      player.destroy();
    }
    this.players.clear();
  }

  /**
   * Destroy manager
   */
  destroy(): void {
    this.clear();
    this.container.destroy();
  }
}
